<script setup lang="ts">
import { computed, nextTick, ref } from 'vue';
import type { Emits, Props } from './types';
import TopTagIcon from '../tagIcon/tagIcon.vue';
import TopPopupListItem from '@/components/popup/popup/listItem.vue';

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const name = defineModel<Props['name']>('name', {
	required: true,
});

const elName = ref<HTMLElement | null>(null);

/**
 * @todo Удалить через пол года после выхода версии firefox с поддержкой contenteditable plaintext-only, включая бета версии
 */
const firefoxProps = computed(() => {
	if (navigator.userAgent.indexOf('Firefox') != -1) {

		return {
			contenteditable: inEdit.value,
			onpaste: (event: Event) => event.preventDefault(),
		};
	}

	return {};
});

/**
 * Включен режим редактирвоания
 */
const inEdit = ref(false);

/**
 * Включить режим редактирование
 */
const turnToEdit = async () => {
	inEdit.value = true;

	await nextTick();

	elName.value?.focus();
};

/**
 * Применить редактирование
 */
const editCommit = () => {
	const name = elName.value?.innerText;
	if (!name) return editCancel();

	if (elName.value) elName.value.innerText = name;

	inEdit.value = false;

	emit('update:name', name);
};

/**
 * Отменить редактирование
 */
const editCancel = async () => {
	if (elName.value) elName.value.innerText = props.name;

	inEdit.value = false;
};

/**
 * Применить выбор
 */
const changeSelect = (e: MouseEvent) => {
	// в режиме редактирования выбор не применяется
	if (inEdit.value) return;

	// тег запрещено выбирать
	if (props.disabled) return;

	let toState: Props['state'] = 'selected';

	if (props.canExclude) {
		if (e.ctrlKey || e.metaKey) {
			toState = 'excluded';
		}
	}

	if (props.state == toState) {
		toState = '';
	}

	if (toState === '') emit('unselect');
	if (toState === 'selected') emit('select');
	if (toState === 'excluded') emit('exclude');
};
</script>

<template>
	<TopPopupListItem
		:class="{
			'top-tagSelector_tagListItem': true,
			'top-tagSelector_tagListItem-inEdit': inEdit,
			'top-tagSelector_tagListItem-disabled': disabled,
			'top-tagSelector_tagListItem-canExclude': canExclude,
			'top-tagSelector-active': !!state,
			'top-tagSelector-excluded': state === 'excluded'
		}"
		@click.stop="changeSelect"
	>
		<TopTagIcon
			:id
			:name
			:colorId
			:state
		/>

		<span
			ref="elName"
			class="top-tagSelector_tagListItemName"
			:contenteditable="inEdit ? 'plaintext-only' : false"
			:="firefoxProps"
			@keydown.enter.stop="editCommit"
			@keydown.esc.stop="editCancel"
		>
			{{ name }}
		</span>

		<template v-if="editable">
			<span
				v-if="!inEdit"
				data-top-icon=""
				class="top-tagSelector_edit"
				@click="turnToEdit"
			></span>

			<span
				v-else
				data-top-icon=""
				class="top-tagSelector_edit"
				@click.stop="editCommit"
			></span>
		</template>
	</TopPopupListItem>
</template>

<style>
.top-tagSelector_tagListItem.top-popup_listItem {
	padding-top: var(--top-padding-2) !important;
	padding-bottom: var(--top-padding-2) !important;
	gap: 10px;
}

.top-tagSelector_tagListItemName {
	border-radius: var(--top-radius-1);
	width: 120px;
	padding: 4px 2px;
	margin: -4px -2px;
	text-overflow: ellipsis;
	overflow: hidden;
	flex-grow: 1;
}

/* редактирование */
.top-tagSelector_edit {
	--top-icon-size: 18px;
	--top-icon-color: var(--color-text-3);

	width: 20px;
	flex-grow: 0;
}

.top-tagSelector_edit:before {
	line-height: 0;
}

.top-tagSelector_edit { opacity: 0; }
.top-tagSelector_edit:hover {
	--top-icon-color: var(--color-text-primary);
}

.top-tagSelector_tagListItem:hover .top-tagSelector_edit,
.top-tagSelector_tagListItem-inEdit .top-tagSelector_edit {
	cursor: pointer;
	opacity: 1;
}

.top-tagSelector_tagListItem-inEdit {
	background: var(--color-layout-front-3) !important;
	outline: 1px dashed var(--color-line-2);
	outline-offset: -1px;
}

.top-tagSelector_tagListItem-inEdit .top-tagSelector_tagListItemName {
	cursor: text;
	background: var(--color-layout-front-1);
}

/* disabled */
.top-tagSelector_tagListItem-disabled {
	cursor: not-allowed !important;
	opacity: 0.85;
}
.top-tagSelector_tagListItem-disabled:hover {
	background: inherit;
}
.top-tagSelector_tagListItem-disabled [data-tag_id] {
	opacity: 0.6;
}
</style>
